/**
 * <TutorStudentsPage> — l'onglet « Mes élèves » de l'espace tuteur (page).
 *
 * `MyStudentsTabScreen.js`, rhabillé : le titre et ses deux gestes — « Activité », qui ouvre « Générer
 * une activité » pour plusieurs élèves à la fois (seulement quand le tuteur a des élèves), et la
 * relecture de la liste — puis ses élèves, un par ligne (`<TutorStudentRow>`), dans UNE carte-liste.
 * Aucun élève : « Aucun élève ». L'ancien écran n'avait ni filtre ni recherche : la page n'en invente pas.
 *
 * Toucher un élève ouvre sa fiche, poussée, ou à droite au large (`<TutorStudentsTwoPane>`, qui borne la
 * page à son volet par `columnWidth` et marque l'élève ouvert par `openStudentId`).
 *
 * Contrôlée : les élèves, le chargement, la relecture et l'erreur arrivent en props ; chaque geste
 * remonte avec son élève. « Générer une activité » est une surface à part, que la page ne monte pas.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Title } from "../../atoms/typography/Title"
import { CardList } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { RefreshButton } from "../../molecules/RefreshButton"
import { TutorStudentRow } from "../../organisms/TutorStudentRow"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { type UITutorStudent } from "../../../ports/primary/ui/types/tutorEleves"
import { TutorStudentsSkeleton } from "./TutorStudentsSkeleton"

const TEXTS = {
    title: "Mes élèves",
    generate: "Activité",
    generateHint: "Générer une activité pour plusieurs élèves",
    empty: "Aucun élève",
}

export type TutorStudentsPageProps = {
    students: UITutorStudent[]
    onOpenProfile?: (student: UITutorStudent) => void
    /** Ses non-lus touchés : sa fiche, sur Messages. */
    onOpenMessages?: (student: UITutorStudent) => void
    /** L'élève dont la fiche est ouverte à côté : sa ligne est marquée. */
    openStudentId?: string | number | null
    /** Dans le volet d'un gabarit à deux volets : la largeur de la colonne. */
    columnWidth?: number
    /** « Activité » — générer une activité commune. Monté seulement quand il y a des élèves. */
    onGenerateForStudents?: () => void

    refreshing?: boolean
    onRefresh?: () => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

/**
 * Mémoïsée : au large, la fiche ouverte à côté vit dans le même écran, et chacune de ses mises à jour
 * (une frappe, un vocal qui avance) ne doit pas redessiner toute la liste.
 */
export const TutorStudentsPage = React.memo(function TutorStudentsPage(props: TutorStudentsPageProps) {
    const {
        students,
        onOpenProfile,
        onOpenMessages,
        openStudentId = null,
        columnWidth = PARENT_CONTENT_WIDTH,
        onGenerateForStudents,
        refreshing = false,
        onRefresh,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            loading={loading}
            skeleton={<TutorStudentsSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={columnWidth}
        >
            <Stack gap="4">
                <Row justify="between" align="center">
                    <Title size="text-2xl">{TEXTS.title}</Title>
                    <Row gap="1" align="center">
                        {students.length > 0 && onGenerateForStudents != null && (
                            <Button
                                label={TEXTS.generate}
                                iconName="plus"
                                size="sm"
                                onPress={onGenerateForStudents}
                                accessibilityHint={TEXTS.generateHint}
                            />
                        )}
                        {onRefresh != null && <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />}
                    </Row>
                </Row>

                {students.length === 0 ? (
                    <EmptyState iconName="user-graduate" title={TEXTS.empty} />
                ) : (
                    <CardList>
                        {students.map((student) => (
                            <TutorStudentRow
                                key={student.id}
                                student={student}
                                selected={student.id === openStudentId}
                                onOpenProfile={onOpenProfile != null ? () => onOpenProfile(student) : undefined}
                                onOpenMessages={onOpenMessages != null ? () => onOpenMessages(student) : undefined}
                                testID={`tutor-student-${student.id}`}
                            />
                        ))}
                    </CardList>
                )}
            </Stack>
        </PageTemplate>
    )
})
